import React, { useEffect } from 'react';
import styles from './Presentation.module.css';
import { useTheme } from '../../Themes/ThemeContext';

const Presentation = () => {
    const { theme: currentTheme, toggleTheme } = useTheme();

    useEffect(() => {
        document.title = "Transforma EditLaTeX - Presentation Slide Deck";

        // Determine initial theme from URL parameter or default to 'blue'
        const urlParams = new URLSearchParams(window.location.search);
        const urlTheme = urlParams.get('theme');

        // We set a small timeout to let ThemeContext finish its initial mount fetch from localStorage
        setTimeout(() => {
            if (urlTheme) {
                toggleTheme({ value: urlTheme });
            } else {
                toggleTheme({ value: 'blue' });
            }
        }, 50);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Theme definitions matching the global index.css variables
    const themeSwatches = [
        { id: 'light', color: '#5147c2', label: 'Primary Light' },
        { id: 'dark', color: '#9389ff', label: 'Primary Dark' },
        { id: 'blue', color: '#00bff2', label: 'Cyan Blue' },
        { id: 'green', color: '#2e850f', label: 'Emerald Green' },
        { id: 'black', color: '#313135', label: 'Midnight Black' }
    ];

    return (
        <div className={styles.presentationContainer}>

            {/* Theme Switcher Overlay */}
            <div className={styles.themeSwitcher}>
                {themeSwatches.map((theme) => (
                    <div
                        key={theme.id}
                        className={`${styles.swatch} ${currentTheme === theme.id ? styles.active : ''}`}
                        style={{ backgroundColor: theme.color }}
                        onClick={() => toggleTheme({ value: theme.id })}
                        title={`Switch to ${theme.label}`}
                    />
                ))}
            </div>

            {/* Slide 1: Hero */}
            <section className={`${styles.slide} ${styles.heroSlide}`}>
                <div className={styles.slideContent}>
                    <img src={process.env.PUBLIC_URL + "/images/white-logo.png"} alt="Transforma Logo" className={styles.heroLogo} />
                    <h1 className={styles.title}>Transforma EditLaTeX</h1>
                    <p className={styles.subtitle}>
                        A modern, easy-to-use cloud platform changing how publishers, editors, and authors work together to build flawless publications.
                    </p>
                </div>
                <div className={styles.scrollIndicator}>↓</div>
                <div className={styles.slideCounter}>01 / 14</div>
            </section>

            {/* Slide 2: Copy Editing Perspective */}
            <section className={`${styles.slide} ${styles.contentSlide}`}>
                <div className={styles.slideContent}>
                    <h2 className={styles.sectionTitle}>The Editor's Workspace</h2>
                    <div className={styles.twoColumn}>
                        <div className={styles.textColumn}>
                            <ul className={styles.featureList}>
                                <li><strong>All-In-One View:</strong> See the LaTeX code, the live PDF preview, and author queries side-by-side. No more switching between different windows.</li>
                                <li><strong>Powerful Search & Replace:</strong> Use advanced tools like Regex to find and fix styling issues across the entire document quickly and safely.</li>
                                <li><strong>Clear Error Messages:</strong> Instead of confusing LaTeX error codes, the system points editors directly to the exact line of code that needs fixing.</li>
                                <li><strong>Track Every Change:</strong> Every single edit is recorded. Editors can always see who changed what, and easily reverse mistakes.</li>
                            </ul>
                        </div>
                        <div className={styles.visualColumn}>
                            <div className={styles.presentationImageWrap}>
                                <img src="/images/presentation/three_pane.png" alt="Three-panel screen showing code, PDF, and queries" className={styles.presentationImage} />
                                <div className={styles.imageCaption}>Three-panel screen showing code, PDF, and queries</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className={styles.scrollIndicator}>↓</div>
                <div className={styles.slideCounter}>02 / 14</div>
            </section>

            {/* Slide 3: Author Proofing */}
            <section className={`${styles.slide} ${styles.darkSlide}`}>
                <div className={styles.slideContent}>
                    <h2 className={styles.sectionTitle}>Simple Author Proofing</h2>
                    <div className={styles.twoColumn}>
                        <div className={styles.visualColumn}>
                            <div className={styles.presentationImageWrap}>
                                <img src="/images/presentation/author.png" alt="Clean, easy-to-use dashboard for authors" className={styles.presentationImage} />
                                <div className={styles.imageCaption}>Clean, easy-to-use dashboard for authors</div>
                            </div>
                        </div>
                        <div className={styles.textColumn}>
                            <ul className={styles.featureList}>
                                <li><strong>No Installation Needed:</strong> Authors just click a link and start editing in their web browser. No complex LaTeX software setup required.</li>
                                <li><strong>Easy to Use:</strong> We designed a clean, simple dashboard so authors can focus on fixing their content, not learning a new software tool.</li>
                                <li><strong>Instant Feedback:</strong> Whenever authors type a change or answer a question, the PDF updates immediately so they can see how it looks.</li>
                                <li><strong>Faster Approvals:</strong> Because authors can quickly review and approve their pages online, publications get finished much faster.</li>
                            </ul>
                        </div>
                    </div>
                </div>
                <div className={styles.scrollIndicator}>↓</div>
                <div className={styles.slideCounter}>03 / 14</div>
            </section>

            {/* Slide 4: Real-time Collaboration */}
            <section className={`${styles.slide} ${styles.contentSlide}`}>
                <div className={styles.slideContent}>
                    <h2 className={styles.sectionTitle}>Working Together in Real-Time</h2>
                    <div className={styles.twoColumn}>
                        <div className={styles.textColumn}>
                            <ul className={styles.featureList}>
                                <li><strong>True Teamwork:</strong> Authors and multiple editors can all be typing and working on the exact same document at the very same time.</li>
                                <li><strong>Live Cursors:</strong> See colored cursors and names jumping around the screen, so you always know where your teammates are editing.</li>
                                <li><strong>Project Permissions:</strong> Managers can easily set rules on who is allowed to edit the text, and who is only allowed to leave comments.</li>
                                <li><strong>Safe Saving:</strong> Even if two people edit the same paragraph, the system safely merges the changes so no one's work is ever lost.</li>
                            </ul>
                        </div>
                        <div className={styles.visualColumn}>
                            <div className={styles.presentationImageWrap}>
                                <img src="/images/presentation/collab.png" alt="Multiple colored cursors and live users typing" className={styles.presentationImage} />
                                <div className={styles.imageCaption}>Multiple colored cursors and live users typing</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className={styles.scrollIndicator}>↓</div>
                <div className={styles.slideCounter}>04 / 14</div>
            </section>

            {/* Slide 5: Publisher Integration */}
            <section className={`${styles.slide} ${styles.darkSlide}`}>
                <div className={styles.slideContent}>
                    <h2 className={styles.sectionTitle}>Built for Publishers</h2>
                    <div className={styles.twoColumn}>
                        <div className={styles.visualColumn}>
                            <div className={styles.presentationImageWrap}>
                                <img src="/images/presentation/download.png" alt="Publisher dashboard with export and project buttons" className={styles.presentationImage} />
                                <div className={styles.imageCaption}>Publisher dashboard with export and project buttons</div>
                            </div>
                        </div>
                        <div className={styles.textColumn}>
                            <ul className={styles.featureList}>
                                <li><strong>Fits Right In:</strong> Whether you publish small journals or huge book series, EditLaTeX connects perfectly into your current workflow.</li>
                                <li><strong>One-Click Downloads:</strong> Get everything you need at the end of the project—the final PDF, clean LaTeX files, and all author comments—with one button.</li>
                                <li><strong>Automatic XML Generation:</strong> Even if you don't usually use XML, our platform automatically builds perfect XML files for modern e-readers and websites.</li>
                                <li><strong>Always Online:</strong> Our platform is built to handle the busiest times of the year without slowing down or crashing.</li>
                            </ul>
                        </div>
                    </div>
                </div>
                <div className={styles.scrollIndicator}>↓</div>
                <div className={styles.slideCounter}>05 / 14</div>
            </section>

            {/* Slide 6: Document Consistency & Styles */}
            <section className={`${styles.slide} ${styles.contentSlide}`}>
                <div className={styles.slideContent}>
                    <h2 className={styles.sectionTitle}>Automatic Document Styling</h2>
                    <div className={styles.twoColumn}>
                        <div className={styles.textColumn}>
                            <ul className={styles.featureList}>
                                <li><strong>"Running Styles" Tool:</strong> Save hours of time by letting the system automatically apply formatting rules across the entire book or journal.</li>
                                <li><strong>Consistent Branding:</strong> Ensures every journal looks exactly the way it should, automatically fixing citations, fonts, and equation numbering.</li>
                                <li><strong>Clean Up Author Code:</strong> The system automatically fixes weird or custom author shortcuts and turns them into clean, standard code.</li>
                                <li><strong>Safe Updates:</strong> Makes huge, document-wide styling changes perfectly, without ever breaking the way the PDF builds.</li>
                            </ul>
                        </div>
                        <div className={styles.visualColumn}>
                            <div className={styles.presentationImageWrap}>
                                <img src="/images/presentation/styles.png" alt="Tool panel showing automatic style cleanup rules" className={styles.presentationImage} />
                                <div className={styles.imageCaption}>Tool panel showing automatic style cleanup rules</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className={styles.scrollIndicator}>↓</div>
                <div className={styles.slideCounter}>06 / 14</div>
            </section>

            {/* Slide 7: Centralized Comments & Queries */}
            <section className={`${styles.slide} ${styles.darkSlide}`}>
                <div className={styles.slideContent}>
                    <h2 className={styles.sectionTitle}>No More Email Chains</h2>
                    <div className={styles.twoColumn}>
                        <div className={styles.visualColumn}>
                            <div className={styles.presentationImageWrap}>
                                <img src="/images/presentation/three_pane.png" alt="Comments and queries attached directly to the text" className={styles.presentationImage} />
                                <div className={styles.imageCaption}>Comments and queries attached directly to the text</div>
                            </div>
                        </div>
                        <div className={styles.textColumn}>
                            <ul className={styles.featureList}>
                                <li><strong>Direct Comments:</strong> Editors can leave sticky notes and questions attached directly to a specific sentence in the document.</li>
                                <li><strong>Fast Responses:</strong> When an author answers a question, the change is verified instantly on the live document.</li>
                                <li><strong>Mandatory Fixes:</strong> The system won't let a project finish until every single editor question has been answered and resolved.</li>
                                <li><strong>Status Dashboard:</strong> Managers can view a simple list showing exactly how many questions are left open before a book is ready to print.</li>
                            </ul>
                        </div>
                    </div>
                </div>
                <div className={styles.scrollIndicator}>↓</div>
                <div className={styles.slideCounter}>07 / 14</div>
            </section>

            {/* Slide 8: Table of Contents (TOC) */}
            <section className={`${styles.slide} ${styles.contentSlide}`}>
                <div className={styles.slideContent}>
                    <h2 className={styles.sectionTitle}>Easy Navigation for Big Books</h2>
                    <div className={styles.twoColumn}>
                        <div className={styles.textColumn}>
                            <ul className={styles.featureList}>
                                <li><strong>Smart Sidebar:</strong> EditLaTeX automatically creates a clickable Table of Contents on the side of your screen, showing every chapter and section.</li>
                                <li><strong>Jump Around Fast:</strong> Working on a 500-page book? Just click "Chapter 4" in the sidebar and the editor leaps right to that specific spot.</li>
                                <li><strong>Catch Mistakes Early:</strong> Missing a chapter heading? The visual sidebar makes it obvious if the book's structure is messed up before you even print.</li>
                                <li><strong>Quick Cross-References:</strong> If you see "Figure 2.1" in the text, you can click on it and the editor will bring you right to that image immediately.</li>
                            </ul>
                        </div>
                        <div className={styles.visualColumn}>
                            <div className={styles.presentationImageWrap}>
                                <img src="/images/presentation/toc.png" alt="A clickable sidebar outlining chapters and sections" className={styles.presentationImage} />
                                <div className={styles.imageCaption}>A clickable sidebar outlining chapters and sections</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className={styles.scrollIndicator}>↓</div>
                <div className={styles.slideCounter}>08 / 14</div>
            </section>

            {/* Slide 9: MathView */}
            <section className={`${styles.slide} ${styles.darkSlide}`}>
                <div className={styles.slideContent}>
                    <h2 className={styles.sectionTitle}>Instant Math Previews</h2>
                    <div className={styles.twoColumn}>
                        <div className={styles.visualColumn}>
                            <div className={styles.presentationImageWrap}>
                                <img src="/images/presentation/math.png" alt="A complex math equation displayed right next to code" className={styles.presentationImage} />
                                <div className={styles.imageCaption}>A complex math equation displayed right next to code</div>
                            </div>
                        </div>
                        <div className={styles.textColumn}>
                            <ul className={styles.featureList}>
                                <li><strong>No Wait Time:</strong> You don't have to rebuild the massive PDF document just to check if you spelled a math equation correctly.</li>
                                <li><strong>See As You Type:</strong> When typing complex math symbols, a visual preview pops up on the screen, letting you check your work instantly.</li>
                                <li><strong>Clean Code:</strong> This feature helps authors proofread quickly, but keeps the background math code perfectly clean for the publisher.</li>
                                <li><strong>Perfect for Science:</strong> A massive speed boost for authors writing heavily mathematical physics or engineering documents.</li>
                            </ul>
                        </div>
                    </div>
                </div>
                <div className={styles.scrollIndicator}>↓</div>
                <div className={styles.slideCounter}>09 / 14</div>
            </section>

            {/* Slide 10: PDF to Editor Sync (SyncTeX) */}
            <section className={`${styles.slide} ${styles.contentSlide}`}>
                <div className={styles.slideContent}>
                    <h2 className={styles.sectionTitle}>Click the PDF, Find the Code</h2>
                    <div className={styles.twoColumn}>
                        <div className={styles.textColumn}>
                            <ul className={styles.featureList}>
                                <li><strong>Two-Way Connection:</strong> Our platform mathematically links the text on the final PDF back to the exact line of raw code that created it.</li>
                                <li><strong>Fix Typos Instantly:</strong> Spot a misspelled word on Page 84? Just click the word on the PDF, and your cursor jumps to that exact line in the editor!</li>
                                <li><strong>No More Hunting:</strong> Editors save hours of time because they never have to manually scroll through thousands of lines of text to find a mistake.</li>
                                <li><strong>Visually Confident:</strong> It gives authors confidence because they can see exactly how the code they type changes the final printed page.</li>
                            </ul>
                        </div>
                        <div className={styles.visualColumn}>
                            <div className={styles.presentationImageWrap}>
                                <img src="/images/presentation/toc.png" alt="Clicking a PDF paragraph instantly highlighting the code" className={styles.presentationImage} />
                                <div className={styles.imageCaption}>Clicking a PDF paragraph instantly highlighting the code</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className={styles.scrollIndicator}>↓</div>
                <div className={styles.slideCounter}>10 / 14</div>
            </section>

            {/* Slide 11: Workflow MAP (Consolidated Visual Flow) */}
            <section className={`${styles.slide} ${styles.darkSlide}`}>
                <div className={styles.slideContent}>
                    <h2 className={styles.sectionTitle}>The EditLaTeX Automatic Pipeline</h2>

                    <div className={styles.workflowMapContainer}>

                        {/* ROW 1: Nodes 1 to 4 -> Left To Right */}
                        <div className={styles.workflowRow}>

                            <div className={styles.workflowStep}>
                                <div className={styles.stepIconWrap}>🔍</div>
                                <div className={styles.stepTitle}>1. Document Check</div>
                                <div className={styles.stepDesc}>We automatically check the author's uploaded files to make sure nothing is broken or missing.</div>
                            </div>

                            <div className={styles.arrowRight}>⇨</div>

                            <div className={styles.workflowStep}>
                                <div className={styles.stepIconWrap}>🏗️</div>
                                <div className={styles.stepTitle}>2. Pre-Structuring</div>
                                <div className={styles.stepDesc}>We safely organize the messy document templates the authors originally wrote.</div>
                            </div>

                            <div className={styles.arrowRight}>⇨</div>

                            <div className={styles.workflowStep}>
                                <div className={styles.stepIconWrap}>⚙️</div>
                                <div className={styles.stepTitle}>3. Code Cleanup</div>
                                <div className={styles.stepDesc}>We force all the code to follow a single, easy-to-read standard formatting rule.</div>
                            </div>

                            <div className={styles.arrowRight}>⇨</div>

                            <div className={styles.workflowStep}>
                                <div className={styles.stepIconWrap}>📚</div>
                                <div className={styles.stepTitle}>4. Reference Merge</div>
                                <div className={styles.stepDesc}>We organize all the bibliography citations so they look identical across the entire book.</div>

                                {/* Downwards Arrow connecting Row 1 to Row 2 */}
                                <div className={styles.arrowVertical}></div>
                            </div>

                        </div>

                        {/* ROW 2: Nodes 5 to 8 <- Right To Left (Flex-Reverse) */}
                        <div className={`${styles.workflowRow} ${styles.reverse}`}>

                            <div className={styles.workflowStep}>
                                <div className={styles.stepIconWrap}>🌐</div>
                                <div className={styles.stepTitle}>5. XML Generation</div>
                                <div className={styles.stepDesc}>We automatically map and build the digital XML text for websites and eReaders.</div>
                            </div>

                            <div className={styles.arrowLeft}>⇦</div>

                            <div className={styles.workflowStep}>
                                <div className={styles.stepIconWrap}>✅</div>
                                <div className={styles.stepTitle}>6. Auto-Pagination</div>
                                <div className={styles.stepDesc}>The system automatically builds the PDF pages perfectly, without needing manual help.</div>
                            </div>

                            <div className={styles.arrowLeft}>⇦</div>

                            <div className={styles.workflowStep}>
                                <div className={styles.stepIconWrap}>🗄️</div>
                                <div className={styles.stepTitle}>7. Core Databases</div>
                                <div className={styles.stepDesc}>We use fast databases and core templates to process thousands of pages quickly.</div>
                            </div>

                            <div className={styles.arrowLeft}>⇦</div>

                            <div className={styles.workflowStep}>
                                <div className={styles.stepIconWrap}>🚀</div>
                                <div className={styles.stepTitle}>8. Final Delivery</div>
                                <div className={styles.stepDesc}>The project is finished perfectly using the specific LaTeX program (PDF, Xe, or Lua) the publisher needs.</div>
                            </div>

                        </div>

                    </div>
                </div>
                <div className={styles.scrollIndicator}>↓</div>
                <div className={styles.slideCounter}>11 / 14</div>
            </section>

            {/* Slide 12: Backend Stack Overview */}
            <section className={`${styles.slide} ${styles.contentSlide}`}>
                <div className={styles.slideContent}>
                    <h2 className={styles.sectionTitle}>The Technology Behind It</h2>
                    <div className={styles.backendGrid}>
                        <div className={styles.backendCard}>
                            <div className={styles.backendCardTitle}>🐍 Python Engine</div>
                            <div className={styles.backendCardDesc}>Python acts as the 'brain' of the platform, safely taking care of all user clicks, saving files, and running the website quickly.</div>
                        </div>
                        <div className={styles.backendCard}>
                            <div className={styles.backendCardTitle}>🐬 Secure Database</div>
                            <div className={styles.backendCardDesc}>We use a powerful MySQL database to securely remember all user passwords, who owns which project, and keep comments safe.</div>
                        </div>
                        <div className={styles.backendCard}>
                            <div className={styles.backendCardTitle}>⚡ High Speed Caching</div>
                            <div className={styles.backendCardDesc}>When multiple people are typing at the same time, we use 'Redis' memory tools to make sure no one ever experiences website lag.</div>
                        </div>
                        <div className={styles.backendCard}>
                            <div className={styles.backendCardTitle}>⚙️ Smart Compilers</div>
                            <div className={styles.backendCardDesc}>Our platform is smart enough to switch between older and newer LaTeX engines (<strong>pdflatex, xelatex, lualatex</strong>) behind the scenes based on what the book needs.</div>
                        </div>
                    </div>
                </div>
                <div className={styles.scrollIndicator}>↓</div>
                <div className={styles.slideCounter}>12 / 14</div>
            </section>

            {/* Slide 13: Docker Containerization */}
            <section className={`${styles.slide} ${styles.darkSlide}`}>
                <div className={styles.slideContent}>
                    <h2 className={styles.sectionTitle}>Safe, Individual Sandboxes 🐳</h2>
                    <div className={styles.twoColumn}>
                        <div className={styles.visualColumn} style={{ flexDirection: 'column', textAlign: 'center' }}>
                            <div className={styles.imagePlaceholder} style={{ borderStyle: 'solid', minHeight: 'auto' }}>
                                <div className={styles.hugeIcon} style={{ marginBottom: '20px' }}>📦</div>
                                <h3 style={{ fontSize: '2rem', margin: '0 0 10px 0' }}>Docker Containers</h3>
                                <p style={{ fontSize: '1.2rem', margin: 0 }}>Isolated Box</p>
                            </div>
                        </div>
                        <div className={styles.textColumn}>
                            <p style={{ fontSize: '1.4rem', marginBottom: '25px', lineHeight: '1.6' }}>
                                A major part of our speed and safety comes from using <strong>Docker Containers</strong>. Every single time a book builds its PDF, it happens in its own private, isolated digital box:
                            </p>
                            <ul className={styles.featureList}>
                                <li><strong>No Interference:</strong> Because every project runs in its own box, one broken project can never accidentally crash another publisher's project.</li>
                                <li><strong>Perfect Consistency:</strong> Say goodbye to the "it works on my computer" problem. Every box runs the exact same software version reliably.</li>
                                <li><strong>Handling Giant Crowds:</strong> When 500 authors try to run their work at the same time, our servers instantly create 500 new boxes to handle the workload without slowing down.</li>
                                <li><strong>Crash Prevention:</strong> If a user types terrible code that freezes forever, it only freezes their one little box, while the rest of the website stays perfectly online.</li>
                            </ul>
                        </div>
                    </div>
                </div>
                <div className={styles.slideCounter}>13 / 14</div>
            </section>

            {/* Slide 14: Thank You */}
            <section className={`${styles.slide} ${styles.heroSlide}`}>
                <div className={styles.slideContent}>
                    <img src={process.env.PUBLIC_URL + "/images/white-logo.png"} alt="Transforma Logo" className={styles.heroLogo} />
                    <h1 className={styles.title} style={{ fontSize: '5rem', marginBottom: '30px' }}>Thank You</h1>
                    <p className={styles.subtitle} style={{ fontSize: '2rem', maxWidth: '800px' }}>
                        Ready to experience the future of digital typesetting? Let's build something incredible together.
                    </p>
                </div>
                <div className={styles.slideCounter}>14 / 14</div>
            </section>

        </div>
    );
};

export default Presentation;
